import { X, ZoomIn, ZoomOut, RotateCw, ImageOff } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";

// Full-screen receipt lightbox with hand-rolled pinch + double-tap zoom + pan.
// We deliberately keep it dependency-free so it works on every Android WebView
// version. Never open the base64 data URL in a new tab — that's the crash we
// fixed earlier.
const MIN_SCALE = 1;
const MAX_SCALE = 6;

const ReceiptViewer = ({ src, onClose }) => {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [rotation, setRotation] = useState(0);
  // True when the stored receipt can't be decoded by this browser (old HEIC
  // uploads pre-HEIF-support) — show a real message instead of a broken icon.
  const [loadErr, setLoadErr] = useState(false);
  // Refs used inside touch/pointer handlers so we don't stale-close over state.
  const pointers = useRef(new Map()); // pointerId -> {x, y}
  const pinchStart = useRef(null); // { dist, scale, cx, cy, tx, ty }
  const panStart = useRef(null); // { x, y, tx, ty }
  const lastTap = useRef(0);

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); setRotation(0); }, []);

  // Esc closes; opening a new receipt resets zoom.
  useEffect(() => { if (src) { reset(); setLoadErr(false); } }, [src, reset]);
  useEffect(() => {
    if (!src) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [src, onClose]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const applyZoomAt = (nextScale, cx, cy) => {
    // Keep the point under the fingers stationary while scaling.
    setScale((prevScale) => {
      const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      const ratio = s / prevScale;
      setTx((prevTx) => cx - ratio * (cx - prevTx));
      setTy((prevTy) => cy - ratio * (cy - prevTy));
      return s;
    });
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      // Start a pinch gesture.
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      pinchStart.current = {
        dist,
        scale,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
        tx,
        ty,
      };
      panStart.current = null;
    } else if (pointers.current.size === 1 && scale > 1) {
      // Start a pan (only meaningful when zoomed in).
      panStart.current = { x: e.clientX, y: e.clientY, tx, ty };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const start = pinchStart.current;
      const nextScale = clamp((start.scale * dist) / (start.dist || 1), MIN_SCALE, MAX_SCALE);
      // Also let the pinch centre drift with the fingers so it feels natural.
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      const ratio = nextScale / start.scale;
      setScale(nextScale);
      setTx(cx - ratio * (start.cx - start.tx));
      setTy(cy - ratio * (start.cy - start.ty));
    } else if (pointers.current.size === 1 && panStart.current) {
      const start = panStart.current;
      setTx(start.tx + (e.clientX - start.x));
      setTy(start.ty + (e.clientY - start.y));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;
    // Double-tap toggle: single-finger, close taps.
    if (e.pointerType === "touch" && pointers.current.size === 0) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        if (scale > 1.05) reset();
        else applyZoomAt(2.5, e.clientX, e.clientY);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    applyZoomAt(scale * (1 + delta), e.clientX, e.clientY);
  };

  const onDoubleClick = (e) => {
    if (scale > 1.05) reset();
    else applyZoomAt(2.5, e.clientX, e.clientY);
  };

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex touch-none items-center justify-center overflow-hidden bg-black/95"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="receipt-viewer"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        data-testid="receipt-viewer-close"
        aria-label="Close receipt"
        className="absolute right-3 top-3 z-10 rounded-full border border-white/25 bg-black/60 p-2 text-white/90 hover:bg-black/80"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/15 bg-black/70 px-2 py-1 text-xs text-white/90">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); applyZoomAt(scale / 1.5, window.innerWidth / 2, window.innerHeight / 2); }}
          data-testid="receipt-viewer-zoom-out"
          aria-label="Zoom out"
          className="rounded-full p-1 hover:bg-white/10"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="font-mono text-[10px] tracking-widest text-white/70">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); applyZoomAt(scale * 1.5, window.innerWidth / 2, window.innerHeight / 2); }}
          data-testid="receipt-viewer-zoom-in"
          aria-label="Zoom in"
          className="rounded-full p-1 hover:bg-white/10"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setRotation((r) => (r + 90) % 360); setTx(0); setTy(0); }}
          data-testid="receipt-viewer-rotate"
          aria-label="Rotate 90°"
          title="Rotate 90°"
          className="rounded-full p-1 hover:bg-white/10"
        >
          <RotateCw className="h-4 w-4" />
        </button>
        {(scale > 1.05 || rotation !== 0) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); reset(); }}
            data-testid="receipt-viewer-reset"
            className="ml-1 rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest hover:bg-white/10"
          >
            Reset
          </button>
        )}
      </div>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={onDoubleClick}
        className="flex h-full w-full select-none items-center justify-center"
        style={{ touchAction: "none" }}
      >
        <div
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: "0 0",
            transition: pointers.current.size ? "none" : "transform 120ms ease-out",
          }}
        >
          {loadErr ? (
            <div className="flex max-w-xs flex-col items-center gap-2 text-center" data-testid="receipt-viewer-error">
              <ImageOff className="h-8 w-8 text-white/40" />
              <p className="text-sm font-semibold text-white/80">This receipt couldn't be displayed</p>
              <p className="text-xs leading-relaxed text-white/50">
                The stored file is unreadable — re-attach the receipt on the expense to fix it.
              </p>
            </div>
          ) : (
            <img
              src={src}
              alt="Receipt"
              draggable={false}
              onError={() => setLoadErr(true)}
              className="max-h-[100vh] max-w-[100vw] object-contain shadow-2xl"
              style={{
                transform: `rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: "transform 200ms ease-out",
              }}
              data-testid="receipt-viewer-image"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptViewer;
